import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {
  Assignment,
  AccessTime,
  EmojiEvents,
  History,
  PlayArrow,
  CheckCircle,
  Cancel,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Exam, ExamAttempt, Course } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const ExamDetail: React.FC = () => {
  const theme = useTheme();
  const { courseId, examId } = useParams<{ courseId: string; examId: string }>();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [attempts, setAttempts] = useState<ExamAttempt[]>([]);
  const [activeAttempt, setActiveAttempt] = useState<ExamAttempt | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!courseId || !examId) return;
      
      try {
        setLoading(true);
        // Fetch course info
        const { results: allCourses } = await apiService.getPublishedCourses(1, 500);
        const courseData = allCourses.find(c => c.hashed === courseId);
        if (!courseData) throw new Error('Course not found');
        setCourse(courseData);

        // Fetch exam info
        const allExams = await apiService.getExamsByCourse(courseData.id);
        const examData = allExams.find(e => e.hashed === examId);
        if (!examData) throw new Error('Exam not found');
        setExam(examData);

        // Fetch user attempts
        const allAttempts = await apiService.getMyExamAttempts();
        
        // Handle if allAttempts is undefined or not an array
        if (!allAttempts || !Array.isArray(allAttempts)) {
          console.error('allAttempts is not an array:', allAttempts);
          setActiveAttempt(null);
          setAttempts([]);
          return;
        }
        
        // Check for active (unsubmitted) attempt
        const activeAttemptFound = allAttempts.find((a: ExamAttempt) => {
          // Try both exam_id and exam.id for compatibility
          const examIdFromField = a.exam_id;
          const examIdFromObject = a.exam?.id;
          const isThisExam = examIdFromField === examData.id || examIdFromObject === examData.id;
          const isNotSubmitted = !a.submitted_at;
          
          return isThisExam && isNotSubmitted;
        });
        
        setActiveAttempt(activeAttemptFound || null);
        
        // Filter only submitted attempts for history
        const examAttempts = allAttempts.filter((a: ExamAttempt) => a.exam_id === examData.id && a.submitted_at);
        setAttempts(examAttempts.sort((a: ExamAttempt, b: ExamAttempt) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()));
        
      } catch (error) {
        console.error('Error fetching exam data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, examId]);

  if (loading) return <LoadingSpinner />;
  if (!exam || !course) return <Alert severity="error">Exam or Course not found</Alert>;

  const bestScore = attempts.length > 0 ? Math.max(...attempts.map(a => a.score || 0)) : 0;
  const isPassed = attempts.some(a => a.is_passed);
  const latestAttempt = attempts[0];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Courses', path: '/courses' },
          { label: course.name, path: `/courses/${course.hashed}` },
          { label: exam.title }
        ]}
      />

      <Box sx={{ mt: 4, mb: 6 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(`/courses/${course.hashed}`)}
          sx={{ mb: 3 }}
        >
          Back to Course
        </Button>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          {/* Main Info */}
          <Box sx={{ flex: 2 }}>
            <Paper sx={{ p: 4, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)', boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Assignment sx={{ color: '#f59e0b', fontSize: '2.5rem' }} />
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {exam.title}
                </Typography>
              </Box>

              {exam.description ? (
                <Box 
                  sx={{ 
                    mb: 4,
                    color: 'text.secondary',
                    '& p': { 
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      marginBottom: '0.5rem'
                    },
                    '& ul, & ol': {
                      paddingLeft: '1.5rem',
                      marginBottom: '1rem'
                    },
                    '& li': {
                      marginBottom: '0.5rem'
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: exam.description }}
                />
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Test your knowledge and earn your certification.
                </Typography>
              )}

              <Divider sx={{ mb: 4 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{exam.duration_minutes ? `${exam.duration_minutes} minutes` : 'No time limit'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Passing Score</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <EmojiEvents sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{exam.passing_score}%</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Attempts</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <History sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{attempts.length} attempts</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Active Attempt Warning */}
              {activeAttempt && !isPassed && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 3 }}
                  icon={<AccessTime />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    You have an exam in progress!
                  </Typography>
                  <Typography variant="body2">
                    Started: {new Date(activeAttempt.started_at).toLocaleString()}
                  </Typography>
                </Alert>
              )}

              {/* Success message when passed */}
              {isPassed && (
                <Alert 
                  severity="success" 
                  icon={<CheckCircle />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    🎉 Congratulations! You have passed this exam!
                  </Typography>
                  <Typography variant="body2">
                    Best Score: {bestScore}% | You can download your certificate by clicking the Download Certificate button in Exam Result Page, or contact admin if you want it to be sent to your email.
                  </Typography>
                </Alert>
              )}

              {/* Only show exam action button if not passed */}
              {!isPassed && (
                <Box sx={{ p: 3, backgroundColor: activeAttempt ? 'rgba(245, 158, 11, 0.05)' : 'rgba(16, 185, 129, 0.05)', borderRadius: 2, border: activeAttempt ? '1px dashed #f59e0b' : '1px dashed #10b981' }}>
                  <Typography variant="h6" sx={{ color: activeAttempt ? '#f59e0b' : '#10b981', mb: 1, fontWeight: 700 }}>
                    {activeAttempt ? 'Continue your exam' : 'Ready to start?'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {activeAttempt 
                      ? 'Your progress is saved. Click below to resume where you left off.'
                      : `Make sure you have a stable internet connection. ${exam.duration_minutes ? 'The timer will start as soon as you begin.' : ''}`
                    }
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrow />}
                    onClick={() => navigate(`/courses/${course.hashed}/exams/${exam.hashed}/take`)}
                    sx={{
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      background: activeAttempt 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                      '&:hover': {
                        background: activeAttempt
                          ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                          : 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                      }
                    }}
                  >
                    {activeAttempt ? 'Resume Exam' : (attempts.length > 0 ? 'Retake Exam' : 'Start Exam')}
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Sidebar Info */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Your Summary
                </Typography>

                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Box sx={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    backgroundColor: isPassed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    border: `4px solid ${isPassed ? '#10b981' : '#e2e8f0'}`
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: isPassed ? '#10b981' : 'text.disabled' }}>
                      {bestScore}%
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">Best Score</Typography>
                  {isPassed && (
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Exam Passed" 
                      color="success" 
                      sx={{ mt: 2, fontWeight: 700 }}
                    />
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                  Recent Attempts
                </Typography>
                {attempts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">You haven't taken this exam yet.</Typography>
                ) : (
                  <List disablePadding>
                    {attempts.slice(0, 5).map((attempt, index) => (
                      <ListItem key={attempt.id} disableGutters sx={{ pb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {attempt.is_passed ? (
                            <CheckCircle sx={{ color: '#10b981', fontSize: '1rem' }} />
                          ) : (
                            <Cancel sx={{ color: '#ef4444', fontSize: '1rem' }} />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={`Attempt ${attempts.length - index}: ${attempt.score}%`}
                          secondary={new Date(attempt.submitted_at || '').toLocaleDateString()}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Button 
                          size="small" 
                          onClick={() => navigate(`/courses/${course.hashed}/exams/${exam.hashed}/result/${attempt.id}`)}
                        >
                          Details
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ExamDetail;
